import { useState } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { MailIcon, PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { RoleByIdSelector, RoleSelectorSkeleton } from "@/features/members/components/RoleSelector";
import { InviteUserSchema, inviteUserSchema } from "@/features/projects/schemas";
import { inviteUser } from "@/features/projects/services/project";
import { Project } from "@/features/projects/types";
import Error from "@/shared/components/Error";
import Success from "@/shared/components/Success";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getProjectRoles } from "@/features/members/services/role";
import { Role } from "@/features/members/types";
import { AppError } from "@/shared/lib/utils/errors";

interface InviteMemberFormProps {
  project: Project;
  trigger?: React.ReactNode;
}

export default function InviteMemberForm({ project, trigger }: InviteMemberFormProps) {
  const [open, setOpen] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: roles, isPending: rolesLoading } = useQuery<Role[], AppError | Error>({
    queryKey: [QUERY_KEYS.GET_PROJECT_ROLES],
    queryFn: getProjectRoles,
    staleTime: QUERY_EXPIRATIONS.GET_PROJECT_ROLES,
    enabled: open,
  });

  const form = useForm<InviteUserSchema>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: {
      projectId: project.id,
      emailAddress: "",
      roleId: "",
    },
  });

  const mutation = useMutation<string, AppError | Error, InviteUserSchema>({
    mutationFn: inviteUser,
    onSuccess: (_, values) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_BY_ID, project.id], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_INVITATIONS, project.id], exact: true });
      setInvitedEmail(values.emailAddress);
      form.reset({ projectId: project.id, emailAddress: "", roleId: "" });
    },
  });

  function handleOpenChange(next: boolean) {
    if (!next) {
      form.reset();
      mutation.reset();
      setInvitedEmail(null);
    }
    setOpen(next);
  }

  async function onSubmit(values: InviteUserSchema) {
    setInvitedEmail(null);
    mutation.mutate(values);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" className="shrink-0">
            <PlusIcon />
            Invite member
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>Invite a member</DialogTitle>
              <DialogDescription>
                Send an invitation to join <span className="font-semibold text-foreground">{project.name}</span>. They'll receive an email with a link to accept.
              </DialogDescription>
            </DialogHeader>
            {mutation.isError && (
              <div className="mt-4">
                <Error title="Unable to send invitation" error={mutation.error} />
              </div>
            )}
            {mutation.isSuccess && invitedEmail && (
              <div className="mt-4">
                <Success title="Invitation sent" description={`${invitedEmail} has been added to the pending invites list below.`} />
              </div>
            )}
            <FieldGroup className="py-4">
              <Controller
                name="projectId"
                control={form.control}
                render={({ field }) => (
                  <Field className="sr-only">
                    <Input type="hidden" {...field} />
                  </Field>
                )}
              />
              <Controller
                name="emailAddress"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>Email address</FieldLabel>
                    <Input {...field} id={field.name} type="email" placeholder="colleague@example.com" autoComplete="off" disabled={mutation.isPending} aria-invalid={fieldState.invalid} />
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
              <Controller
                name="roleId"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel>Role</FieldLabel>
                    {rolesLoading ? <RoleSelectorSkeleton /> : <RoleByIdSelector roles={roles ?? []} value={field.value} onChange={field.onChange} disabled={mutation.isPending} />}
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
            </FieldGroup>
            <DialogFooter showCloseButton>
              <Button type="submit" disabled={mutation.isPending || rolesLoading}>
                <MailIcon />
                {mutation.isPending ? "Sending..." : "Send invite"}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
